import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { TooltipModule } from '@sb/tooltip';
import { IconComponent } from '@sb/ui';

import { ControlMessages, RuleOption } from '../../../forms/control-messages.component';

// if we start using more transformations we should collect them in a shared file
function trimStringAttribute(value: string | undefined): string {
  return value?.trim() ?? '';
}

@Component({
  selector: 'sb-form-field',
  templateUrl: './sb-form-field.component.html',
  standalone: true,
  imports: [ControlMessages, NgClass, IconComponent, TooltipModule, ControlMessages],
})
export class SbFormFieldComponent implements AfterViewInit {
  @HostBinding('class')
  public readonly hostClasses = 'flex flex-col gap-1';

  public labelFor: string;

  @ViewChild(ControlMessages, { static: false })
  public controlMessages: ControlMessages;

  @Input()
  public hint?: string | SafeHtml;

  @Input()
  public control: AbstractControl;

  @Input()
  public label?: string | SafeHtml;

  /**
   * @deprecated Use `tooltip instead`.
   */
  @Input()
  public set info(value: string) {
    this.tooltip = value;
  }

  @Input({ transform: trimStringAttribute })
  public set tooltip(value: string) {
    this._tooltip = value;
  }

  public get tooltip() {
    return this._tooltip;
  }
  private _tooltip: string;

  @Input()
  public errorWhen: RuleOption[] = ['dirty'];

  @Input()
  public parentCheck: string[];

  /**
   * @deprecated not in use anymore
   */
  @Input()
  public fullWidth = false;
  /**
   * @deprecated not in use anymore
   */
  @Input()
  public noPadding = false;

  @Input()
  public set overrideError(value: boolean) {
    this._overrideError = value;
    this.updateAriaInvalid();
  }
  public get overrideError() {
    return this._overrideError;
  }
  private _overrideError: boolean;

  @Input()
  public overrideErrorMessage: string;

  @Input()
  public required = false;
  @Input()
  public showLabel = true;
  @Input()
  public hideErrorMessages = false;

  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly cd = inject(ChangeDetectorRef);

  // class binding can be removed when bootstrap is removed
  @HostBinding('class.has-danger')
  public get invalid(): boolean {
    return this.controlMessages?.showError || this.overrideError;
  }

  private get inputEl(): HTMLElement | undefined {
    const mainElement = this.elementRef.nativeElement.querySelector('input, select, textarea, time-input');
    if (mainElement) {
      return mainElement;
    }
    // secondary element used by sb-select
    return this.elementRef.nativeElement.querySelector('button');
  }

  public ngAfterViewInit(): void {
    this.updateAriaInvalid();

    if (this.inputEl?.id) {
      this.labelFor = this.inputEl.id;
      // trigger change detection to update labelFor without causing ExpressionChangedAfterItHasBeenCheckedError
      this.cd.detectChanges();
    }
  }

  public onShowErrorChange(): void {
    this.updateAriaInvalid();
  }

  private updateAriaInvalid(): void {
    if (!this.inputEl) {
      return;
    }

    if (this.invalid) {
      this.inputEl.setAttribute('aria-invalid', 'true');
    } else {
      this.inputEl.removeAttribute('aria-invalid');
    }

    this.cd.detectChanges();
  }
}
